import subYears from "date-fns/subYears";
import parseISO from "date-fns/parseISO";
import lightFormat from "date-fns/lightFormat";
import getYear from "date-fns/getYear";
import { useQuery } from "@apollo/client";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { palette } from "lib/miolo";
import CvmMaterialFactsQuery from "lib/queries/CvmMaterialFactsQuery";
import type {
  ICvmMaterialFactsQuery,
  ICvmMaterialFactsQueryVariables,
} from "lib/queries/__generated__/CvmMaterialFactsQuery.generated";
import ClipLoader from "react-spinners/ClipLoader";
import classNames from "components/ui/classNames";

interface IIpesProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

export default function Ipes({ issuer }: IIpesProps) {
  const { data, loading } = useQuery<ICvmMaterialFactsQuery, ICvmMaterialFactsQueryVariables>(CvmMaterialFactsQuery, {
    variables: {
      filterByIssuerCnpj: issuer.cnpj,
      deliveryDateStart: lightFormat(subYears(new Date(), 1), "yyyy-MM-dd"),
      deliveryDateEnd: lightFormat(new Date(), "yyyy-MM-dd"),
      filterByCategories: null,
      filterByIssuers: null,
      limit: 30,
      cursor: null,
    },
  });

  if (loading) {
    return (
      <div className="p-6 text-center">
        <ClipLoader size={28} color={palette.royalBlue} loading={true} />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md bg-white p-2">
      <table className="min-w-full border-collapse border-inherit text-left indent-0 text-sm">
        <thead className="bg-white">
          <tr>
            <th className="px-4 py-3 font-semibold uppercase text-neutral-700">Tipo</th>
            <th className="px-4 py-3 text-center font-semibold uppercase text-neutral-700">Publicado Em</th>
          </tr>
        </thead>

        <tbody>
          {data?.cvmMaterialFacts.nodes.map((node) => {
            const deliveryDate = parseISO(node.deliveryDate);
            const yearOfReference = getYear(deliveryDate);

            return (
              <tr
                key={node.protocol}
                className={classNames(
                  "group border-t border-neutral-200",
                  "hover:border-t-transparent [&:hover+tr]:border-t-transparent",
                )}
              >
                <td
                  className={classNames(
                    "px-4 py-3 group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <div className="grid items-center">
                    {node.category === "Fato Relevante" && (
                      <div className="mr-3">
                        <div className="flex h-3 w-3 items-center justify-center rounded-lg bg-red-600" />
                      </div>
                    )}
                    {node.category === "Aviso aos Acionistas" && node.type === "Outros avisos" && (
                      <div className="mr-3">
                        <div className="flex h-3 w-3 items-center justify-center rounded-lg bg-yellow-600" />
                      </div>
                    )}
                    {node.category === "Dados Econômico-Financeiros" && (
                      <div className="mr-3">
                        <div className="flex h-3 w-3 items-center justify-center rounded-lg bg-blue-600" />
                      </div>
                    )}
                    {node.category === "Comunicado ao Mercado" && (
                      <div className="mr-3">
                        <div className="flex h-3 w-3 items-center justify-center rounded-lg bg-green-800" />
                      </div>
                    )}

                    <a
                      href={node.url}
                      rel="noopener noreferrer"
                      target="_blank"
                      className="inline-grid gap-y-2 overflow-hidden text-ellipsis whitespace-nowrap text-blue-800 no-underline"
                    >
                      <div>
                        <span
                          className={classNames(
                            "inline-flex border-b border-dashed border-transparent",
                            "text-base font-medium text-neutral-700",
                            "hover:border-neutral-900",
                          )}
                        >
                          {node.category}
                        </span>
                      </div>

                      {node.type && <span className="flex text-sm text-slate-600">↪ {node.type || "-"}</span>}
                    </a>
                  </div>
                </td>

                <td
                  className={classNames(
                    "px-4 py-3 text-center group-hover:bg-neutral-50 group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <div className="grid gap-y-1">
                    <span className="text-base font-medium text-slate-600">
                      {lightFormat(deliveryDate, "dd/MM/yyyy")}
                    </span>

                    <div>
                      <span className="text-sm text-slate-500">Referente à {yearOfReference}</span>
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
